import { useState, useRef, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { DraftConversation, DraftFileMetadata, UploadedFile } from '../types';
import { DRAFT_STORAGE_KEY } from '../constants';
import { getFieldTypeFromCategory, getAPIKeyFromField } from '../utils';

interface UseDraftManagementProps {
  currentChatId?: string;
  field: string;
  onFieldChange?: (field: string) => void;
}

export const useDraftManagement = ({
  currentChatId,
  field,
  onFieldChange,
}: UseDraftManagementProps) => {
  const organizationId = localStorage.getItem('current_organization') || '';

  const getInitialApiKey = (f: string) =>
    getFieldTypeFromCategory(f) || getAPIKeyFromField(f) || 'current_process';

  const [selectValue, setSelectValue] = useState(() => getInitialApiKey(field));
  const [details, setDetails] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  const selectValueRef = useRef(selectValue);
  useEffect(() => {
    selectValueRef.current = selectValue;
  }, [selectValue]);

  // Sync field from parent
  useEffect(() => {
    if (field) {
      const apiKey = getInitialApiKey(field);
      if (apiKey !== selectValueRef.current) {
        setSelectValue(apiKey);
      }
    }
  }, [field]);

  const onFieldChangeRef = useRef(onFieldChange);
  useEffect(() => {
    onFieldChangeRef.current = onFieldChange;
  }, [onFieldChange]);

  // ─── Single shared storage key per org ──────────────────────────────────────
  // Draft is NOT per-category: same draft is shown for ALL field types.
  const storageKey = organizationId
    ? `${DRAFT_STORAGE_KEY}_${organizationId}`
    : DRAFT_STORAGE_KEY;

  // ─── Storage helpers ─────────────────────────────────────────────────────────

  const readDraft = useCallback((): DraftConversation | null => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? (JSON.parse(saved) as DraftConversation) : null;
    } catch {
      return null;
    }
  }, [storageKey]);

  const writeDraft = useCallback(
    (text: string, files: DraftFileMetadata[]) => {
      try {
        const existing = (() => {
          try {
            const s = localStorage.getItem(storageKey);
            return s ? (JSON.parse(s) as DraftConversation) : null;
          } catch {
            return null;
          }
        })();
        const now = new Date().toISOString();
        const draft: DraftConversation = {
          draftId: existing?.draftId || uuidv4(),
          details: text,
          fieldType: selectValueRef.current,
          files,
          createdAt: existing?.createdAt || now,
          updatedAt: now,
        };
        if (text.trim() || files.length > 0) {
          localStorage.setItem(storageKey, JSON.stringify(draft));
        } else {
          localStorage.removeItem(storageKey);
        }
      } catch (e) {
        console.error('Failed to save draft:', e);
      }
    },
    [storageKey]
  );

  const clearDraftFromStorage = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
    } catch (e) {
      console.error('Failed to clear draft:', e);
    }
  }, [storageKey]);

  // ─── Mount: load shared draft ─────────────────────────────────────────────────

  const draftLoadedRef = useRef(false);
  useEffect(() => {
    if (currentChatId) return;
    if (draftLoadedRef.current) return;
    draftLoadedRef.current = true;

    const draft = readDraft();
    if (draft) {
      setDetails(draft.details);
      if (draft.files?.length) {
        setUploadedFiles(
          draft.files.map((f) => ({
            id: f.id,
            name: f.name,
            file: new File([], f.name),
            url: f.url,
            filePath: f.filePath,
            fileSize: f.fileSize,
            isUploading: false,
            isAnalyzing: false,
          }))
        );
      } else {
        setUploadedFiles([]);
      }
    }
  }, [currentChatId, readDraft]);

  // ─── Auto-save on every text/file change ─────────────────────────────────────

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (currentChatId) return;
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const fileMetadata: DraftFileMetadata[] = uploadedFiles
      .filter((f) => !f.isUploading && !f.failed && (f.url || f.filePath))
      .map((f) => ({
        id: f.id,
        name: f.name,
        url: f.url,
        filePath: f.filePath,
        fileSize: f.fileSize,
      }));

    writeDraft(details, fileMetadata);
  }, [details, uploadedFiles]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Return ───────────────────────────────────────────────────────────────────

  return {
    details,
    setDetails,
    uploadedFiles,
    setUploadedFiles,
    selectValue,
    setSelectValue,
    clearDraftFromStorage,
    readDraft,
    writeDraft,
  };
};
